import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as _ from 'lodash';
import {
  ACTION_NAMES,
  API_RESPONSE,
  AUTH_ERROR_CODES,
  ERRORS,
  SIGNUP_PROCESS_STEPS,
  SLICES
} from '../../constants/index';
import {
  createResetAction,
  localStorageKeyFormatter,
  updateLocalStorage
} from '../../utils/index';
import axiosInstance from '../../axiosInstance';
import { setShowAlert } from '../utilts/utilsSlice';
import {
  AuthSliceState,
  AuthTokens,
  NewUserPayload,
  LoginBodyParams,
  ResetPasswordParamsType
} from './interfaces';
import {
  ERROR_MESSAGE,
  ERROR_TYPES,
  TOAST_TYPE
} from '../../utils/constantTypes';
import { RootState } from '../store';
import { sendOtpSms, verifyOtpSms } from '../services/auth';

export const initialNewUserPayload = {
  isEmailOptVerified: false,
  email: '',
  secondaryEmail: '',
  firstName: '',
  fullName: '',
  lastName: '',
  licenseNumber: '',
  licenseState: '',
  managementSoftware: '',
  notificationSetting: { email: false, sms: false, push: true },
  password: '',
  phoneNumber: { countryCode: '', number: '' },
  secondaryPhoneNumber: { countryCode: '', number: '' },
  role: '',
  title: '',
  userName: ''
};

const initialState: AuthSliceState = {
  isAuthenticated: false,
  isOtpVerified: false,
  MFARequired: false,
  loading: false,
  showModal: false,
  isPmsValid: false,
  tokens: {
    accessToken: '',
    refreshToken: '',
    sikkaToken: '',
    stripePublishKey: ''
  },
  error: null,
  newUserPayload: initialNewUserPayload,
  userId: '',
  practiceId: '',
  otpToken: '',
  currentForm: 'login',
  currentPage: 'login',
  resetPasswordToken: ''
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    storeUserPayload: (state, action) => {
      // localStorage.setItem('newUserPayload', JSON.stringify(action.payload));
      state.newUserPayload = action.payload;
    },
    resetErrors: (state) => {
      state.error = null;
    },
    toggleModal: (state, action) => {
      state.showModal = action.payload;
    },
    setPmsModal: (state, action) => {
      state.isPmsValid = action.payload;
    },
    setCurrentForm: (state, action) => {
      state.currentForm = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setLoader: (state, action) => {
      state.loading = action.payload;
    },
    setResetPasswordToken: (state, action) => {
      state.resetPasswordToken = action.payload;
    },
    setOtpVerificationStep: (state, action) => {
      state.newUserPayload.isEmailOptVerified =
        action.payload.isEmailOptVerified;
    },
    setOtpToken: (state, action) => {
      state.otpToken = action.payload.otpToken;
    },
    setIsOtpVerified: (state, action) => {
      state.isOtpVerified = action.payload;
      if (action.payload) {
        localStorage.setItem('isOtpVerified', 'true');
      } else {
        localStorage.removeItem('isOtpVerified');
      }
    },
    resetAuth: createResetAction(initialState),
    setAuthError: (state, action) => {
      state.error = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loginAsync.pending, (state) => {
      state.loading = true;
      state.error = '';
    });
    builder.addCase(loginAsync.fulfilled, (state, { payload }) => {
      state.loading = false;
      const tokens = _.get(payload, 'tokens', payload);
      state.isAuthenticated = true;
      state.tokens = tokens;
      state.MFARequired = _.get(payload, 'MFARequired', false);
      const keys = localStorageKeyFormatter<AuthTokens>(tokens);
      updateLocalStorage(keys);
    });
    builder.addCase(loginAsync.rejected, (state, action) => {
      const code = _.get(action, 'payload.code');
      if (code === AUTH_ERROR_CODES.PRACTICE_INCOMPLETE) {
        state.userId = _.get(action, 'payload.userId', '');
        state.currentForm = 'practice';
        // PracticeForm logs in after create via newUserPayload credentials
        state.newUserPayload = {
          ...state.newUserPayload,
          email: _.get(action, 'meta.arg.username', state.newUserPayload.email),
          password: _.get(
            action,
            'meta.arg.password',
            state.newUserPayload.password
          )
        };
        state.error = '';
        state.loading = false;
        return;
      }
      const msg = _.get(
        action,
        'payload.msg',
        _.get(action, 'payload.message')
      );
      state.error = msg;
      state.loading = false;
    });
    builder.addCase(validateSessionAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(validateSessionAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.isAuthenticated = action.payload.isAuthenticated;
      state.tokens = action.payload.tokens;
    });
    builder.addCase(validateSessionAsync.rejected, (state, action) => {
      state.loading = false;
      localStorage.clear();
    });
    builder.addCase(logoutAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(logoutAsync.fulfilled, (state) => {
      localStorage.clear();
      return initialState;
    });
    builder.addCase(logoutAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(createUserAsync.pending, (state) => {
      state.error = null;
      state.loading = true;
    });
    builder.addCase(createUserAsync.fulfilled, (state, action) => {
      state.loading = false;
      const stepType = _.get(action, 'payload.stepType');
      const newUserPayload = _.get(action, 'payload.newUserPayload');
      const otpToken = _.get(action, 'payload.otpToken');
      const isEmailOptVerified = _.get(action, 'payload.isEmailOtpVerfied');
      const isMobileOtpVerfied = _.get(action, 'payload.isMobileOtpVerfied');
      const userId = _.get(action, 'payload.userId');
      state.otpToken = otpToken;
      if (stepType === SIGNUP_PROCESS_STEPS.VERIFY_NEW_USER) {
        state.isPmsValid = true;
        // state.showModal = true;
      } else if (stepType === SIGNUP_PROCESS_STEPS.SEND_EMAIL_OTP) {
        state.loading = false;
        state.newUserPayload = newUserPayload;
        state.currentForm = 'sms';
      } else if (stepType == SIGNUP_PROCESS_STEPS.VERIFY_EMAIL_OTP) {
        // state.otpToken = otpToken;
        state.newUserPayload.isEmailOptVerified = isEmailOptVerified;
        state.error = isEmailOptVerified ? '' : 'Please enter the valid code.';
      } else if (stepType == SIGNUP_PROCESS_STEPS.VERIFY_MOBILE_OTP) {
        state.error = isMobileOtpVerfied ? '' : 'Please enter the valid code.';
        if (isMobileOtpVerfied) {
          state.currentForm = 'practice';
          state.userId = userId;
          // state.otpToken = otpToken;
        }
      }
    });
    builder.addCase(createUserAsync.rejected, (state, action) => {
      // const invalidPms = _.get(action, 'payload.invalidPms', false);
      // if (invalidPms) {
      //   state.loading = false;
      //   state.isPmsValid = false;
      //   state.showModal = true;
      //   return;
      // }
      const msg = _.get(
        action,
        'payload.msg',
        _.get(action, 'payload.message', '')
      );
      state.error = msg;
      state.loading = false;
    });
    builder.addCase(createPracticeAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createPracticeAsync.fulfilled, (state, action) => {
      state.error = null;
      state.loading = false;
      // state.currentForm = 'sikka';
      state.currentForm = '';
      state.practiceId = action.payload;
    });
    builder.addCase(createPracticeAsync.rejected, (state, action) => {
      const msg = _.get(
        action,
        'payload.msg',
        _.get(action, 'payload.message')
      );
      state.error = msg;
      state.loading = false;
    });
    builder.addCase(forgotPasswordAsync.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(forgotPasswordAsync.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(forgotPasswordAsync.rejected, (state, action) => {
      const msg = _.get(
        action,
        'payload.msg',
        _.get(action, 'payload.message')
      );
      state.error = msg;
      state.loading = false;
    });
    builder.addCase(resetPasswordAsync.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(resetPasswordAsync.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(resetPasswordAsync.rejected, (state, action) => {
      const msg = _.get(
        action,
        'payload.msg',
        _.get(action, 'payload.message')
      );
      state.error = msg;
      state.loading = false;
    });
    builder.addCase(sendOtpAsync.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(sendOtpAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.otpToken = action.payload;
    });
    builder.addCase(sendOtpAsync.rejected, (state, action) => {
      state.error = action.payload as string;
      state.loading = false;
    });
    builder.addCase(verifyOtpAsync.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(verifyOtpAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.otpToken = '';
      state.isOtpVerified = true;
      localStorage.setItem('isOtpVerified', 'true');
    });
    builder.addCase(verifyOtpAsync.rejected, (state, action) => {
      state.error = action.payload as string;
      state.loading = false;
    });
  }
});

export const loginAsync = createAsyncThunk(
  'auth/loginAsync',
  async (payload: LoginBodyParams, { rejectWithValue, dispatch }) => {
    try {
      const user = await axiosInstance.post('auth/login', payload);
      const data = _.get(user, 'data', {});
      const stripePublishKey = _.get(data, 'stripePublishKey', '');
      const tokens = _.get(data, 'tokens', {});
      const MFARequired = _.get(data, 'MFARequired', false);
      const officeId = _.get(data, 'officeId', null);
      return {
        tokens: { ...tokens, stripePublishKey },
        MFARequired,
        officeId
      };
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const logoutAsync = createAsyncThunk(
  'auth/logoutAsync',
  async (payload: string, { dispatch }) => {
    try {
      await axiosInstance.get('auth/logout', {
        headers: {
          Authorization: `Bearer ${payload}`
        }
      });
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: err?.response?.data?.msg || err?.response?.data?.message,
            type: TOAST_TYPE.ERROR
          })
        );
      }
    }
  }
);

export const validateSessionAsync = createAsyncThunk(
  'auth/validateSession',
  async (payload: AuthTokens, { dispatch, rejectWithValue }) => {
    try {
      const authRes = await axiosInstance.get('auth/validateSession', {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      });
      const authData = _.get(authRes, 'data');
      if (
        authData.msg === API_RESPONSE.SESSION_NOT_FOUND &&
        authData.statusCode === 400
      ) {
        localStorage.clear();
        dispatch(
          setShowAlert({
            show: true,
            message: ERROR_TYPES.INVALID_SESSION,
            type: TOAST_TYPE.ERROR
          })
        );
        return { isAuthenticated: false, tokens: { ...initialState.tokens } };
      }
      return { isAuthenticated: true, tokens: { ...payload } };
    } catch (err) {
      dispatch(
        setShowAlert({
          show: true,
          message: ERROR_TYPES.INVALID_SESSION,
          type: TOAST_TYPE.ERROR
        })
      );
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const createUserAsync = createAsyncThunk(
  'auth/createUser',
  async (_payload: NewUserPayload, { rejectWithValue, dispatch }) => {
    try {
      const userSignUpRes = await axiosInstance.post('auth/signup', {
        ..._payload
      });
      const statusCode = _.get(userSignUpRes, 'data.statusCode');
      const msg = _.get(userSignUpRes, 'data.statusCode');

      const userSignUpData = _.get(userSignUpRes, 'data.data');
      const _otpToken = _.get(_payload, 'otpToken', '');
      const otpToken = _.get(userSignUpData, 'otpToken', _otpToken);
      const userId = _.get(userSignUpData, 'userId', '');
      const newUserPayload = _.omit(_payload, [
        'sendEmailOtp',
        'emailOtp',
        'sendMobileOtp',
        'mobileOtp'
      ]);
      const stepType = _.get(_payload, 'stepType');
      if (stepType === SIGNUP_PROCESS_STEPS.SEND_EMAIL_OTP) {
        return {
          newUserPayload,
          stepType: SIGNUP_PROCESS_STEPS.SEND_EMAIL_OTP,
          otpToken
        };
      } else if (stepType === SIGNUP_PROCESS_STEPS.VERIFY_EMAIL_OTP) {
        if (statusCode === 400) {
          return {
            isEmailOtpVerfied: false,
            stepType: SIGNUP_PROCESS_STEPS.VERIFY_EMAIL_OTP,
            otpToken
          };
        }
        return {
          isEmailOtpVerfied: true,
          stepType: SIGNUP_PROCESS_STEPS.VERIFY_EMAIL_OTP,
          otpToken
        };
      } else if (stepType === SIGNUP_PROCESS_STEPS.VERIFY_MOBILE_OTP) {
        if (statusCode === 400) {
          return {
            isMobileOtpVerfied: false,
            stepType: SIGNUP_PROCESS_STEPS.VERIFY_MOBILE_OTP,
            otpToken
          };
        }
        return {
          isMobileOtpVerfied: true,
          userId,
          stepType: SIGNUP_PROCESS_STEPS.VERIFY_MOBILE_OTP,
          otpToken
        };
      }
      return { newUserPayload, otpToken, stepType };
    } catch (err) {
      const errorMsg = err?.response?.data?.msg || err?.response?.data?.message;
      if (errorMsg === ERRORS.INVALID_PMS) {
        return rejectWithValue({ invalidPms: true });
      }
      const invalidMobileNo =
        errorMsg === ERRORS.INVALID_MOBILE_NUM ||
        errorMsg === ERRORS.INVALID_SECONDARY_MOBILE_NUM;
      // else if (errorMsg === ERRORS.INVALID_MOBILE_NUM) {
      //   return rejectWithValue({
      //     invalidMobileNo: true,
      //     msg: ERROR_MESSAGE.mobileFormatErrorMsg
      //   });
      // }
      await dispatch(
        setShowAlert({
          show: true,
          message: invalidMobileNo
            ? ERROR_MESSAGE.mobileFormatErrorMsg
            : errorMsg,
          type: TOAST_TYPE.ERROR
        })
      );
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const createPracticeAsync = createAsyncThunk(
  'auth/createPractice',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const practiceRes = await axiosInstance.post('practice', {
        ...payload
      });
      const practiceData = _.get(practiceRes, 'data.data');
      const practiceId = _.get(practiceData, 'practiceId');
      return practiceId;
    } catch (err) {
      await dispatch(
        setShowAlert({
          show: true,
          message: err?.response?.data?.msg || err?.response?.data?.message,
          type: TOAST_TYPE.ERROR
        })
      );
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const forgotPasswordAsync = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email }: { email: string }, { dispatch, rejectWithValue }) => {
    try {
      const forgotPasswordRes = await axiosInstance.post(
        'auth/portal-forgot-password',
        { email }
      );
      const msg = _.get(forgotPasswordRes, 'data.msg');
      const statusCode = _.get(forgotPasswordRes, 'data.statusCode');
      await dispatch(
        setShowAlert({
          show: true,
          message: msg,
          type: TOAST_TYPE.SUCCESS
        })
      );
      return { msg, statusCode };
    } catch (err) {
      await dispatch(
        setShowAlert({
          show: true,
          message: err?.response?.data?.msg || err?.response?.data?.message,
          type: TOAST_TYPE.ERROR
        })
      );
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const resetPasswordAsync = createAsyncThunk(
  'auth/resetPassword',
  async (_payload: ResetPasswordParamsType, { dispatch, rejectWithValue }) => {
    try {
      const forgotPasswordRes = await axiosInstance.post(
        'auth/reset-password',
        { ..._payload }
      );
      const msg = _.get(forgotPasswordRes, 'data.msg');
      const statusCode = _.get(forgotPasswordRes, 'data.statusCode');
      await dispatch(
        setShowAlert({
          show: true,
          message: msg,
          type: TOAST_TYPE.SUCCESS
        })
      );
      // to expire all logged in sessions
      localStorage.clear();
      return { msg, statusCode };
    } catch (err) {
      await dispatch(
        setShowAlert({
          show: true,
          message: err?.response?.data?.msg || err?.response?.data?.message,
          type: TOAST_TYPE.ERROR
        })
      );
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const sendOtpAsync = createAsyncThunk(
  `${SLICES.AUTH}/${ACTION_NAMES.SEND_OTP}`,
  async (payload: any, { dispatch, rejectWithValue }) => {
    try {
      const otpRes = await sendOtpSms(payload, { dispatch });
      const otpData = _.get(otpRes, 'data', {});
      const statusCode = _.get(otpRes, 'statusCode');
      if (statusCode === 200) {
        const otpToken = _.get(otpData, 'otpToken');
        return otpToken;
      }
    } catch (err) {
      return rejectWithValue(err?.message);
    }
  }
);

export const verifyOtpAsync = createAsyncThunk(
  `${SLICES.AUTH}/${ACTION_NAMES.VERIFY_OTP}`,
  async (payload: any, { dispatch, rejectWithValue }) => {
    try {
      const otpRes = await verifyOtpSms(payload, { dispatch });
      const statusCode = _.get(otpRes, 'statusCode');
      const otpData = _.get(otpRes, 'data', {});
      if (statusCode !== 200) {
        return rejectWithValue(ERROR_TYPES.INVALID_OTP);
      }
    } catch (err) {
      return rejectWithValue(err?.message);
    }
  }
);

export const {
  storeUserPayload,
  resetErrors,
  toggleModal,
  setCurrentForm,
  setCurrentPage,
  setLoader,
  setResetPasswordToken,
  resetAuth,
  setOtpVerificationStep,
  setOtpToken,
  setAuthError,
  setPmsModal,
  setIsOtpVerified
} = authSlice.actions;

export default authSlice.reducer;

export const getNewUserPayload = (state: RootState) =>
  state?.auth?.newUserPayload;

export const getIsAuthenticated = (state: RootState) =>
  state?.auth?.isAuthenticated;
